import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import type { ServiceCluster } from '@/api/service-clusters';
import { ClusterTriggerBindingsCard } from '@/components/features/services/ClusterTriggerBindingsCard';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, LoadingState } from '@/components/ui';

interface ClusterFollowupBindingsOverviewCardProps {
  tenantIdForApi?: string;
  clusters: ServiceCluster[];
  isLoading: boolean;
  onBindingsChanged?: () => void | Promise<void>;
  /** 08.10.26 — внутри переключателя разделов список кластеров сразу открыт. Не удалять. */
  embedded?: boolean;
}

export function ClusterFollowupBindingsOverviewCard({
  tenantIdForApi,
  clusters,
  isLoading,
  onBindingsChanged,
  embedded = false,
}: ClusterFollowupBindingsOverviewCardProps) {
  const [isCollapsed, setIsCollapsed] = useState(!embedded);

  const visibleClusters = useMemo(
    () =>
      [...clusters]
        .filter((cluster) => cluster.is_active || (cluster.template_count || 0) > 0)
        .sort((a, b) => {
          if (a.is_active !== b.is_active) {
            return Number(b.is_active) - Number(a.is_active);
          }
          if (a.display_order !== b.display_order) {
            return a.display_order - b.display_order;
          }
          return a.name.localeCompare(b.name, 'ru');
        }),
    [clusters]
  );

  const open = embedded || !isCollapsed;

  return (
    <Card className="border-shell-border bg-shell-surface shadow-none">
      {!embedded && (
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle className="text-base font-extrabold tracking-tight text-shell-text">
            Привязки Cluster Follow-up
          </CardTitle>
          <p className="max-w-3xl text-sm text-shell-muted">
            Кластерные follow-up отображаются отдельно от event-driven pipeline, но используют ту же
            generic/override модель выбора шаблона по каналу.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed((prev) => !prev)}
          className="shrink-0 font-semibold"
        >
          {isCollapsed ? <ChevronRight className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}
          {isCollapsed ? 'Показать' : 'Скрыть'}
        </Button>
      </CardHeader>
      )}
      {open && (
        <CardContent className="pt-0">
          {!tenantIdForApi ? (
            <EmptyState
              type="search"
              title="Выберите клинику"
              description="Для просмотра cluster follow-up привязок выберите клинику."
            />
          ) : isLoading ? (
            <LoadingState />
          ) : visibleClusters.length === 0 ? (
            <EmptyState
              type="search"
              title="Нет кластеров для follow-up"
              description="Сначала создайте или активируйте кластеры услуг, затем назначьте follow-up шаблоны."
            />
          ) : (
            <div className="space-y-4">
              {visibleClusters.map((cluster) => (
                <div key={cluster.id} className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={cluster.is_active ? 'success' : 'secondary'}>
                      {cluster.is_active ? 'Активен' : 'Неактивен'}
                    </Badge>
                    {(cluster.service_count || 0) > 0 && (
                      <Badge variant="info">Услуг: {cluster.service_count}</Badge>
                    )}
                    {(cluster.template_count || 0) > 0 && (
                      <Badge variant="warning">Активных связей: {cluster.template_count}</Badge>
                    )}
                  </div>
                  <ClusterTriggerBindingsCard
                    clusterId={cluster.id}
                    title={cluster.name}
                    description=""
                    defaultCollapsed={true}
                    onBindingsChanged={onBindingsChanged}
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
